import { Howl, Howler, HowlOptions } from 'howler';
import {
  MUSIC_TRACKS,
  SFX_GAME,
  SFX_UI,
  AUDIO_CONSTANTS,
} from './AudioConfig';

/**
 * AudioManager - Manages all game audio with Howler.js
 *
 * DESIGN: No sound in space (realistic approach)
 * - Space: Only background music
 * - Planets/Stations: Music + UI SFX
 *
 * Features:
 * - Three volume channels: Master, Music, SFX
 * - Silent fallbacks for missing audio files (no crashes)
 * - Music crossfade transitions
 * - Auto-mute when tab is hidden
 * - localStorage persistence
 */
class AudioManager {
  // Volume levels (0-1)
  private masterVolume: number;
  private musicVolume: number;
  private sfxVolume: number;

  // Sound storage
  private music: Map<string, Howl> = new Map();
  private sfxGame: Map<string, Howl> = new Map();
  private sfxUI: Map<string, Howl> = new Map();

  // Active looping SFX (key → sound id)
  private activeLoops: Map<string, number> = new Map();

  // Per-sound volume multipliers (from config)
  private sfxVolumeMultipliers: Map<string, number> = new Map();

  // Track which sounds actually loaded successfully
  private loadedSounds: Set<string> = new Set();

  // Current music state
  private currentMusicKey: string | null = null;
  private currentMusicId: number | null = null;

  // Mute state
  private isMuted: boolean = false;

  // Initialization flag
  private initialized: boolean = false;

  constructor() {
    // Load saved volumes from localStorage
    this.masterVolume = this.loadVolume(
      AUDIO_CONSTANTS.STORAGE_KEY_MASTER,
      AUDIO_CONSTANTS.DEFAULT_MASTER_VOLUME
    );
    this.musicVolume = this.loadVolume(
      AUDIO_CONSTANTS.STORAGE_KEY_MUSIC,
      AUDIO_CONSTANTS.DEFAULT_MUSIC_VOLUME
    );
    this.sfxVolume = this.loadVolume(
      AUDIO_CONSTANTS.STORAGE_KEY_SFX,
      AUDIO_CONSTANTS.DEFAULT_SFX_VOLUME
    );

    // Set Howler global volume to 1 (we manage volume per-sound)
    Howler.volume(1);
  }

  /**
   * Initialize audio system - loads all sounds
   * Call this once when game starts
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Register music tracks (lazy-loaded on first play to save ~60-90MB of decoded audio memory)
    for (const [key, path] of Object.entries(MUSIC_TRACKS)) {
      this.loadSound(key, path, this.music, {
        loop: true,
        volume: 0, // Start silent, set volume when playing
        preload: false,
      });
    }

    // Load game SFX (combat, mining, explosions)
    for (const [key, config] of Object.entries(SFX_GAME)) {
      if (config.volume !== undefined) this.sfxVolumeMultipliers.set(key, config.volume);
      this.loadSound(key, config.path, this.sfxGame, {
        loop: config.loop ?? false,
        volume: this.getEffectiveSfxVolume(),
      });
    }

    // Load UI SFX
    for (const [key, path] of Object.entries(SFX_UI)) {
      this.loadSound(key, path, this.sfxUI, {
        loop: false,
        volume: this.getEffectiveSfxVolume(),
      });
    }

    // Auto-mute when tab is hidden
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Load a sound with silent fallback if file doesn't exist
   */
  private loadSound(
    key: string,
    path: string,
    storage: Map<string, Howl>,
    options: Partial<HowlOptions> = {}
  ): void {
    const howl = new Howl({
      src: [path],
      preload: true,
      loop: false,
      volume: 1,
      ...options,
      onload: () => {
        this.loadedSounds.add(key);
      },
      onloaderror: () => {
        // Silent fallback - sound will not play but game continues
      },
    });

    storage.set(key, howl);
  }

  private loadVolume(key: string, defaultValue: number): number {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      const val = parseFloat(saved);
      if (!isNaN(val) && val >= 0 && val <= 1) {
        return val;
      }
    }
    return defaultValue;
  }

  private saveVolume(key: string, value: number): void {
    localStorage.setItem(key, value.toString());
  }

  // --- Volume Getters ---

  getMasterVolume(): number {
    return this.masterVolume;
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  getSfxVolume(): number {
    return this.sfxVolume;
  }

  getEffectiveMusicVolume(): number {
    return this.masterVolume * this.musicVolume;
  }

  getEffectiveSfxVolume(): number {
    return this.masterVolume * this.sfxVolume;
  }

  // --- Volume Setters (update live sounds) ---

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.saveVolume(AUDIO_CONSTANTS.STORAGE_KEY_MASTER, this.masterVolume);
    this.updateAllVolumes();
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    this.saveVolume(AUDIO_CONSTANTS.STORAGE_KEY_MUSIC, this.musicVolume);
    this.updateMusicVolume();
  }

  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.saveVolume(AUDIO_CONSTANTS.STORAGE_KEY_SFX, this.sfxVolume);
    // UI SFX volume updated on next play
  }

  private updateAllVolumes(): void {
    this.updateMusicVolume();
    // Spatial SFX volumes are calculated per-play based on distance
    // UI SFX volumes are set on next play
  }

  private updateMusicVolume(): void {
    if (this.currentMusicKey && this.currentMusicId !== null) {
      const howl = this.music.get(this.currentMusicKey);
      if (howl) {
        howl.volume(this.getEffectiveMusicVolume(), this.currentMusicId);
      }
    }
  }

  // --- Game SFX ---

  private getSfxVolumeForKey(key: string): number {
    const multiplier = this.sfxVolumeMultipliers.get(key) ?? 1;
    return this.getEffectiveSfxVolume() * multiplier;
  }

  /**
   * Play a one-shot game sound effect
   */
  playSFX(key: string): void {
    if (!this.loadedSounds.has(key)) return;

    const howl = this.sfxGame.get(key);
    if (!howl) return;

    const id = howl.play();
    howl.volume(this.getSfxVolumeForKey(key), id);
  }

  /**
   * Start a looping game sound effect (e.g. mining_loop)
   */
  playLoopSFX(key: string): void {
    if (this.activeLoops.has(key)) return;
    if (!this.loadedSounds.has(key)) return;

    const howl = this.sfxGame.get(key);
    if (!howl) return;

    const id = howl.play();
    howl.volume(this.getSfxVolumeForKey(key), id);
    this.activeLoops.set(key, id);
  }

  /**
   * Stop a looping game sound effect
   */
  stopLoopSFX(key: string): void {
    const id = this.activeLoops.get(key);
    if (id === undefined) return;

    const howl = this.sfxGame.get(key);
    if (howl) {
      howl.fade(this.getEffectiveSfxVolume(), 0, 300, id);
      setTimeout(() => howl.stop(id), 300);
    }
    this.activeLoops.delete(key);
  }

  // --- UI Audio ---

  /**
   * Play a UI sound effect (non-positional)
   */
  playUI(key: string): void {
    if (!this.loadedSounds.has(key)) return;

    const howl = this.sfxUI.get(key);
    if (!howl) return;

    const id = howl.play();
    howl.volume(this.getEffectiveSfxVolume(), id);
  }

  // --- Music ---

  /**
   * Play a music track with crossfade
   */
  playMusic(key: string, crossfadeDuration: number = AUDIO_CONSTANTS.CROSSFADE_DURATION): void {
    // Same track already playing - do nothing
    if (this.currentMusicKey === key && this.currentMusicId !== null) {
      return;
    }

    const newHowl = this.music.get(key);

    // If howl doesn't exist, something is wrong
    if (!newHowl) {
      return;
    }

    // Check the howl's internal state to see if it's loaded
    const howlState = newHowl.state();

    if (howlState === 'loaded') {
      // Already loaded, play immediately
      this.playMusicInternal(key, newHowl, crossfadeDuration);
    } else if (howlState === 'loading') {
      // Still loading, wait for it
      newHowl.once('load', () => {
        this.playMusicInternal(key, newHowl, crossfadeDuration);
      });
    } else if (howlState === 'unloaded') {
      // Lazy-load: trigger load and play when ready
      newHowl.once('load', () => {
        this.loadedSounds.add(key);
        this.playMusicInternal(key, newHowl, crossfadeDuration);
      });
      newHowl.load();
    }
    // error state - silently ignore
  }

  private playMusicInternal(key: string, newHowl: Howl, crossfadeDuration: number): void {
    // Fade out current music and unload it to free decoded audio memory
    if (this.currentMusicKey && this.currentMusicId !== null) {
      const oldKey = this.currentMusicKey;
      const oldHowl = this.music.get(oldKey);
      if (oldHowl) {
        const oldId = this.currentMusicId;
        oldHowl.fade(oldHowl.volume(oldId) as number, 0, crossfadeDuration, oldId);
        setTimeout(() => {
          oldHowl.stop(oldId);
          // Unload to free decoded PCM memory (~12-18MB per track)
          oldHowl.unload();
          this.loadedSounds.delete(oldKey);
        }, crossfadeDuration);
      }
    }

    // Start new music
    this.currentMusicKey = key;
    this.currentMusicId = newHowl.play();

    // Fade in
    newHowl.volume(0, this.currentMusicId);
    newHowl.fade(0, this.getEffectiveMusicVolume(), crossfadeDuration, this.currentMusicId);
  }

  /**
   * Stop current music with fade out
   */
  stopMusic(fadeOutDuration: number = 500): void {
    if (this.currentMusicKey && this.currentMusicId !== null) {
      const stoppingKey = this.currentMusicKey;
      const howl = this.music.get(stoppingKey);
      if (howl) {
        const id = this.currentMusicId;
        howl.fade(howl.volume(id) as number, 0, fadeOutDuration, id);
        setTimeout(() => {
          howl.stop(id);
          // Unload to free decoded PCM memory
          howl.unload();
          this.loadedSounds.delete(stoppingKey);
        }, fadeOutDuration);
      }
    }
    this.currentMusicKey = null;
    this.currentMusicId = null;
  }

  // --- Mute ---

  mute(): void {
    this.isMuted = true;
    Howler.mute(true);
  }

  unmute(): void {
    this.isMuted = false;
    Howler.mute(false);
  }

  toggleMute(): boolean {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.isMuted;
  }

  getIsMuted(): boolean {
    return this.isMuted;
  }

  // --- Utility ---

  /**
   * Check if a sound is loaded and available
   */
  isLoaded(key: string): boolean {
    return this.loadedSounds.has(key);
  }

  /**
   * Handle tab visibility changes
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      Howler.mute(true);
    } else {
      Howler.mute(this.isMuted);
    }
  };

  /**
   * Clean up resources
   */
  destroy(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Stop all loops
    this.activeLoops.clear();

    // Stop all sounds
    this.music.forEach((howl) => howl.unload());
    this.sfxGame.forEach((howl) => howl.unload());
    this.sfxUI.forEach((howl) => howl.unload());

    this.music.clear();
    this.sfxGame.clear();
    this.sfxUI.clear();
    this.loadedSounds.clear();

    this.currentMusicKey = null;
    this.currentMusicId = null;
    this.initialized = false;
  }
}

// Global singleton instance
export const audioManager = new AudioManager();
